import React, { useState, useCallback } from 'react';
import { Box, Alert, CircularProgress } from '@mui/material';
import {
  AddOutlined as AddIcon,
  CloudDownloadOutlined as DownloadIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import {
  ListPageScaffold,
  ListPageMain,
  ListPageToolbar,
  ListPageFiltersSection,
} from '../../common/list-page';
import UserFilters from '../UserFilters';
import UsersTable from '../UsersTable';
import UserWizard from '../UserWizard';
import UserDetailModal from '../UserDetailModal';
import UserDeleteModal from '../UserDeleteModal';
import { UserBlockModal } from '../UserBlockModal';
import { UserResetPasswordModal } from '../UserResetPasswordModal';
import { UserCreatedPasswordModal } from '../UserCreatedPasswordModal';
import CommonPagination from '../../common/Pagination';
import { useUsersPage } from './UsersPage.hooks';
import { useSettingsUsersPermissions } from '../../../hooks/useSettingsUsersPermissions';
import {
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  StyledSnackbar,
  StyledAlert,
} from './UsersPage.styles';
import EmptyStateMessage from '../../common/EmptyStateMessage';
import { User } from '../../../types/user.types';

const UsersPage: React.FC = () => {
  const usersPerms = useSettingsUsersPermissions();

  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);

  const {
    filters,
    setFilters,
    appliedFilters,
    showMoreFilters,
    setShowMoreFilters,
    roleOptions,
    drawerOpen,
    lockedSearchTerm,
    selectedUser,
    detailModalOpen,
    detailUser,
    deleteModalOpen,
    usersToDelete,
    pagination,
    selectedUsers,
    setSelectedUsers,
    loading,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isSearching,
    error,
    users,
    notification,
    setNotification,
    branchLocations,
    getPrimaryLocationName,
    handleSearch,
    handleReset,
    handleClearAppliedField,
    handleFilterChange,
    handlePaginationChange,
    handleItemsPerPageChange,
    handleAddUser,
    handleEditUser,
    handleViewUser,
    handleDeleteUsers,
    handleDeleteSingleUser,
    handleConfirmDelete,
    handleCloseDeleteModal,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handleBlockUser,
    handleBlockUserDirect,
    handleUnblockUser,
    handleConfirmBlock,
    handleCloseBlockModal,
    blockModalOpen,
    userToBlock,
    handleCloseDrawer,
    handleCloseDetailModal,
    handleCloseNotification,
    handleExportUsers,
    handleSaveUser,
    createdPasswordInfo,
    clearCreatedPasswordInfo,
  } = useUsersPage();

  const handleResetPassword = useCallback(
    (userId: string) => {
      const user = users.find((u) => u.id === userId);
      if (user) {
        setResetPasswordUser(user);
        setResetPasswordModalOpen(true);
      }
    },
    [users],
  );

  const handleResetPasswordSuccess = useCallback(() => {
    setNotification({
      open: true,
      message: 'Contraseña restablecida con éxito',
      severity: 'success',
    });
    handleSearch();
  }, [setNotification, handleSearch]);

  const toggleMoreFilters = () => {
    setShowMoreFilters(!showMoreFilters);
  };

  const handlePageChange = (page: number) => {
    handlePaginationChange({ currentPage: page });
  };

  return (
    <ListPageScaffold>
      <ListPageMain sectionGap={1}>
        <ListPageToolbar>
          {usersPerms.canAddUser && (
            <PrimaryButton
              variant="contained"
              startIcon={<AddIcon />}
              size="small"
              onClick={handleAddUser}
            >
              Añadir
            </PrimaryButton>
          )}

          {usersPerms.canExportUsers && (
            <SecondaryButton
              variant="outlined"
              startIcon={<DownloadIcon />}
              size="small"
              onClick={handleExportUsers}
            >
              Exportar
            </SecondaryButton>
          )}

          {usersPerms.canDeleteUsers && (
            <DangerButton
              variant="outlined"
              startIcon={<DeleteIcon />}
              size="small"
              onClick={handleDeleteUsers}
              disabled={selectedUsers.length === 0 || loading}
            >
              Eliminar
            </DangerButton>
          )}
        </ListPageToolbar>

        <ListPageFiltersSection>
          <UserFilters
            filters={filters}
            setFilters={setFilters}
            appliedFilters={appliedFilters}
            onClearAppliedField={handleClearAppliedField}
            showMoreFilters={showMoreFilters}
            roles={roleOptions}
            lockedSearchTerm={lockedSearchTerm}
            onSearch={handleSearch}
            onReset={handleReset}
            onToggleMoreFilters={toggleMoreFilters}
            onFilterChange={handleFilterChange}
          />
        </ListPageFiltersSection>

        {/* Error message */}
        {error && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4,
              minHeight: '200px',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {/* Loading state */}
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4,
              minHeight: '200px',
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Empty state */}
        {!loading && users.length === 0 && !error && (
          <EmptyStateMessage message="No se encontraron usuarios" minHeight={200} />
        )}

        {/* Users table */}
        {!loading && users.length > 0 && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 0,
              border: 'none',
              bgcolor: 'transparent',
              overflow: 'visible',
              minHeight: 0,
            }}
          >
            <UsersTable
              users={users}
              loading={loading}
              selectedUsers={selectedUsers}
              setSelectedUsers={setSelectedUsers}
              onViewUser={handleViewUser}
              onEditUser={handleEditUser}
              onDeleteUser={handleDeleteSingleUser}
              onBlockUserDirect={handleBlockUserDirect}
              onUnblockUserDirect={handleUnblockUser}
              onResetPassword={handleResetPassword}
              getPrimaryLocationName={getPrimaryLocationName}
            />
          </Box>
        )}

        {/* Pagination */}
        {!loading && users.length > 0 && (
          <Box>
            <CommonPagination
              currentPage={pagination.currentPage}
              itemsPerPage={pagination.itemsPerPage}
              totalItems={pagination.totalItems}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </Box>
        )}
      </ListPageMain>

      {/* User Wizard */}
      <UserWizard
        key={selectedUser?.id ?? 'new-user'}
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
        onSave={handleSaveUser}
        editUser={selectedUser}
        roles={roleOptions}
        locations={branchLocations}
      />

      {/* User Detail Modal */}
      <UserDetailModal
        open={detailModalOpen}
        user={detailUser}
        onClose={handleCloseDetailModal}
        onEditUser={handleEditUser}
        getPrimaryLocationName={getPrimaryLocationName}
      />

      {/* Delete Confirmation Modal */}
      <UserDeleteModal
        open={deleteModalOpen}
        users={usersToDelete}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
      />

      {/* Block Confirmation Modal */}
      <UserBlockModal
        open={blockModalOpen}
        user={userToBlock}
        onClose={handleCloseBlockModal}
        onConfirm={handleConfirmBlock}
      />

      {/* Reset Password Modal */}
      <UserResetPasswordModal
        open={resetPasswordModalOpen}
        user={resetPasswordUser}
        onClose={() => {
          setResetPasswordModalOpen(false);
          setResetPasswordUser(null);
        }}
        onSuccess={handleResetPasswordSuccess}
      />

      <UserCreatedPasswordModal
        open={Boolean(createdPasswordInfo)}
        info={createdPasswordInfo}
        onClose={clearCreatedPasswordInfo}
      />

      {/* Notification Snackbar */}
      <StyledSnackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {notification.successMessage && notification.errorMessage ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              width: '100%',
            }}
          >
            <StyledAlert
              onClose={handleCloseNotification}
              severity="success"
              variant="filled"
              sx={{ width: '100%' }}
            >
              {notification.successMessage}
            </StyledAlert>
            <StyledAlert
              onClose={handleCloseNotification}
              severity="error"
              variant="filled"
              sx={{ width: '100%' }}
            >
              {notification.errorMessage}
            </StyledAlert>
          </Box>
        ) : (
          <StyledAlert
            onClose={handleCloseNotification}
            severity={notification.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {notification.message}
          </StyledAlert>
        )}
      </StyledSnackbar>
    </ListPageScaffold>
  );
};

export default UsersPage;
